import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Mail, User } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { useAuthStore } from '../../store/authStore';
import { MFASetupForm } from './MFASetupForm';

interface RegisterFormData {
  firstName: string;
  lastName: string;
  email: string;
}

type RegistrationStep = 'form' | 'mfa';

interface RegisterFormProps {
  onSuccess?: () => void;
  onLogin?: () => void;
}

export function RegisterForm({ onSuccess, onLogin }: RegisterFormProps) {
  const [step, setStep] = useState<RegistrationStep>('form');
  const [registrationData, setRegistrationData] = useState<RegisterFormData | null>(null);
  const { register, handleSubmit, formState: { errors } } = useForm<RegisterFormData>();
  const { register: registerUser, isLoading } = useAuthStore();
  const { toast } = useToast();

  const onSubmit = async (data: RegisterFormData) => {
    // Store the form data and proceed to MFA setup
    setRegistrationData(data);
    setStep('mfa');
  };

  const handleMFASuccess = async (mfaSecret: string) => {
    if (!registrationData) return;

    try {
      await registerUser({
        firstName: registrationData.firstName,
        lastName: registrationData.lastName,
        email: registrationData.email,
        mfaSecret, // Include MFA secret in registration
      });

      toast({
        title: "Registration Request Submitted",
        description: "Your registration request with MFA has been submitted. You will receive an email once an administrator approves your account.",
      });
      
      onSuccess?.();
    } catch (error) {
      toast({
        title: "Registration Failed",
        description: error instanceof Error ? error.message : "An error occurred during registration",
        variant: "destructive",
      });
    }
  };

  const handleBackToForm = () => {
    setStep('form');
    setRegistrationData(null);
  };

  // Show MFA setup if we're on that step
  if (step === 'mfa') {
    return (
      <MFASetupForm
        onSuccess={handleMFASuccess}
        onBack={handleBackToForm}
        isRegistration={true}
        email={registrationData?.email}
      />
    );
  }

  // Show registration form
  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center">Create Account</CardTitle>
        <CardDescription className="text-center">
          Request access to ISMS Manager - admin approval required
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="firstName"
                  placeholder="John"
                  className="pl-10"
                  {...register('firstName', {
                    required: 'First name is required',
                    pattern: {
                      value: /^[a-zA-ZÀ-ÿ\s'-]+$/,
                      message: 'Invalid characters in first name'
                    }
                  })}
                />
              </div>
              {errors.firstName && (
                <p className="text-sm text-destructive">{errors.firstName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="lastName"
                  placeholder="Doe"
                  className="pl-10"
                  {...register('lastName', {
                    required: 'Last name is required',
                    pattern: {
                      value: /^[a-zA-ZÀ-ÿ\s'-]+$/,
                      message: 'Invalid characters in last name'
                    }
                  })}
                />
              </div>
              {errors.lastName && (
                <p className="text-sm text-destructive">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="john.doe@example.com"
                className="pl-10"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
              />
            </div>
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800 text-center">
              <strong>Next:</strong> After filling out this form, you'll be guided through setting up 
              two-factor authentication (MFA) to secure your account.
            </p>
          </div>

          <div className="space-y-2">
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading}
            >
              Continue to MFA Setup
            </Button>

            <div className="text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <Button
                type="button"
                variant="link"
                className="p-0 h-auto font-normal"
                onClick={onLogin}
              >
                Sign in
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
